import { composeMuxStoryboardStrip } from './mux.utils';
import React from 'react';

interface StoryboardStripProps {
  playbackId: string;
  duration: number;
  value: number;
  onChange?: (t: number) => void;
  onChangeEnd?: (t: number) => void;
  height?: number;
}

interface BackgroundState {
  url: string;
  w: number;
  h: number;
}

const DEFAULT_HEIGHT = 80;
const BORDER_RADIUS = 12;
const PLACEHOLDER_BACKGROUND = '#eee';
const INDICATOR_COLOR = 'rgba(95, 30, 204, 1)';
const INDICATOR_SHADOW = '0 0 0 2px rgba(95, 30, 204, .15)';

const clampValue = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

const calculateTimeFromPosition = (
  clientX: number,
  elementRect: DOMRect,
  duration: number,
): number => {
  const x = clampValue(clientX - elementRect.left, 0, elementRect.width);
  const ratio = elementRect.width ? x / elementRect.width : 0;
  return clampValue(ratio * duration, 0, duration);
};

const calculateProgressPercentage = (value: number, duration: number): number =>
  (value / Math.max(duration, 0.0001)) * 100;

const getKeyboardTimeStep = (
  key: string,
  duration: number,
  currentValue: number,
): number | null => {
  const step = duration / 100;
  const bigStep = duration / 10;

  switch (key) {
    case 'ArrowRight':
      return currentValue + step;
    case 'ArrowLeft':
      return currentValue - step;
    case 'ArrowUp':
      return currentValue + bigStep;
    case 'ArrowDown':
      return currentValue - bigStep;
    case 'Home':
      return 0;
    case 'End':
      return duration;
    default:
      return null;
  }
};

export function StoryboardStrip({
  playbackId,
  duration,
  value,
  onChange,
  onChangeEnd,
  height = DEFAULT_HEIGHT,
}: StoryboardStripProps) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [bg, setBg] = React.useState<BackgroundState | null>(null);

  React.useEffect(() => {
    let alive = true;
    composeMuxStoryboardStrip(playbackId, {
      targetHeight: height,
      maxWidth: 752,
    }).then(({ url, width, height: resultHeight }) => {
      if (!alive) return;
      setBg({ url, w: width, h: resultHeight });
    });
    return () => {
      alive = false;
    };
  }, [playbackId, height]);

  const setByX = (clientX: number, final = false) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const t = calculateTimeFromPosition(clientX, rect, duration);
    onChange?.(t);
    if (final) onChangeEnd?.(t);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const nextTime = getKeyboardTimeStep(e.key, duration, value);
    if (nextTime === null) return;

    const clampedTime = clampValue(nextTime, 0, duration);
    onChange?.(clampedTime);
    onChangeEnd?.(clampedTime);
    e.preventDefault();
  };

  if (!bg) {
    return (
      <div
        style={{
          height,
          borderRadius: BORDER_RADIUS,
          background: PLACEHOLDER_BACKGROUND,
        }}
      />
    );
  }

  const progressPercentage = calculateProgressPercentage(value, duration);

  return (
    <div
      style={{
        overflowX: 'auto',
        overflowY: 'hidden',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div
        ref={ref}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={Math.round(duration * 1000)}
        aria-valuenow={Math.round(value * 1000)}
        tabIndex={0}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture?.(e.pointerId);
          setByX(e.clientX, false);
        }}
        onPointerMove={(e) => {
          if (e.currentTarget.hasPointerCapture?.(e.pointerId))
            setByX(e.clientX, false);
        }}
        onPointerUp={(e) => {
          e.currentTarget.releasePointerCapture?.(e.pointerId);
          setByX(e.clientX, true);
        }}
        onKeyDown={handleKeyDown}
        style={{
          position: 'relative',
          width: bg.w,
          height: bg.h,
          borderRadius: BORDER_RADIUS,
          backgroundImage: `url("${bg.url}")`,
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: `calc(${progressPercentage}% - 1px)`,
            width: 2,
            height: '100%',
            background: INDICATOR_COLOR,
            boxShadow: INDICATOR_SHADOW,
            pointerEvents: 'none',
          }}
        />
      </div>
    </div>
  );
}
